import { ChordCakeState, SectionState } from '../types';
import { EMPTY_SONG_META, songMetaFields, type KeySignature, type SongMeta } from '@chordbaker/music-theory';
import { encodeContainer } from './chordbakerContainer';
import { toWireSong } from './songSchema';

export interface ExportOptions {
  title: string;
  sections: SectionState[];
  key: KeySignature | null;
  /** Song tempo + global time signature; only the set ones are written */
  songMeta?: SongMeta;
  guitarShapes: Record<string, string>;
}

export const exportChordSheet = async (options: ExportOptions): Promise<void> => {
  const { title, sections, key, songMeta = EMPTY_SONG_META, guitarShapes } = options;

  try {
    const chordSheetData: ChordCakeState = {
      version: 4,
      title,
      sections,
      key,
      ...songMetaFields(songMeta),
      guitarShapes,
    };

    // Serialize to JSON in the wire schema version (V3 for the app versions in the field)
    const dataStr = JSON.stringify(toWireSong(chordSheetData), null, 2);

    // Verify JSON validity by parsing it back
    try {
      JSON.parse(dataStr);
    } catch (parseError) {
      throw new Error(`Invalid JSON generated: ${parseError instanceof Error ? parseError.message : 'Unknown error'}`);
    }

    // Wrap in the opaque container (hides raw JSON from text previews in
    // messaging apps)
    const dataBlob = await encodeContainer(dataStr);

    // Create download link
    const link = document.createElement('a');
    link.href = URL.createObjectURL(dataBlob);

    // Generate filename
    const fileName = title.trim()
      ? `${title.trim().replace(/[<>:"/\\|?*]/g, '')}.chordbaker`
      : 'chord_sheet.chordbaker';

    link.download = fileName;
    link.style.display = 'none';

    // Trigger download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up object URL
    setTimeout(() => {
      URL.revokeObjectURL(link.href);
    }, 100);

  } catch (error) {
    console.error('Error exporting chord chart:', error);
    throw new Error(`Error exporting chord chart: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

// Legacy wrapper function for backwards compatibility
export const exportChordSheetToFile = (
  title: string,
  sections: SectionState[],
  key: KeySignature | null,
  guitarShapes: Record<string, string> = {},
): Promise<void> => {
  return exportChordSheet({
    title,
    sections,
    key,
    guitarShapes,
  });
};
